import _ from 'lodash';
import { AnalyticsConsumer } from 'contexts/AnalyticsProvider';
import { useTheme } from '@mui/material/styles';
import { motion, useAnimationControls } from 'framer-motion';
import { TRACK_HERO_DEMO_CTA_CLICKED, PATH_FAQ_SHIPPING } from 'constants';
import AppHeaderSpacer from 'components/Header/AppHeaderSpacer';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import classNames from 'classnames';
import HTMLFlipBook from 'react-pageflip';
import Grid from '@mui/material/Grid';
import Image from 'components/Image';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import makeClasses from 'hooks/useClasses';
import Link from 'next/link';
import Phone from 'components/Home/HeroDemo/Phone';
import DemoProvider, { DemoConsumer } from 'contexts/HeroDemoProvider';
import PropTypes from 'prop-types';
import Sparkles from 'components/Sparkles/Sparkles';
import Stack from '@mui/material/Stack';
import React, {
  useRef, useState, useEffect
} from 'react';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import styles from 'styles/jss/components/landingHeroStyle';

const COVER_COLOR = '#9e173a';
const TITLE = 'Turn your kid\'s artwork into a stunning photo book';
const SUBTITLE = 'Effortless hardcover photo books, designed at the speed of upload';
const IMAGE_ROOT = '/img/marketing/landing/section_hero_demo';
const IMAGE_VERSION = '1.1';

const BOOK_SIZES = {
  xs: { bookWidth: 580, bookHeight: 337 },
  sm: { bookWidth: 640, bookHeight: 372 },
  md: { bookWidth: 700, bookHeight: 407 },
  lg: { bookWidth: 860, bookHeight: 500 }
};

const PHONE_SIZES = {
  xs: { phoneWidth: 173, phoneHeight: 350 },
  sm: { phoneWidth: 213, phoneHeight: 430 },
  md: { phoneWidth: 213, phoneHeight: 430 },
  lg: { phoneWidth: 213, phoneHeight: 430 }
};

const PAGES = [
  {
    image: `${IMAGE_ROOT}/art/salty_book.png?v=${IMAGE_VERSION} `,
    screenMasked: `${IMAGE_ROOT}/art/salty_screen_masked.png?v=${IMAGE_VERSION}`,
    screenShadow: `${IMAGE_ROOT}/art/salty_screen_shadow.png?v=${IMAGE_VERSION}`,
    screenBackground: `${IMAGE_ROOT}/art/salty_screen_floor.jpg?v=${IMAGE_VERSION}`,
    bgColor: 'rgba(132, 173, 189, 1)',
    textColor: '#333',
    title: 'Salty The Pirate'
  },
  {
    image: `${IMAGE_ROOT}/art/kittie_book.png?v=${IMAGE_VERSION}`,
    screenMasked: `${IMAGE_ROOT}/art/kittie_screen_masked.png?v=${IMAGE_VERSION}`,
    screenShadow: `${IMAGE_ROOT}/art/kittie_screen_shadow.png?v=${IMAGE_VERSION}`,
    screenBackground: `${IMAGE_ROOT}/art/kittie_screen_background.jpg?v=${IMAGE_VERSION}`,
    bgColor: 'rgba(144, 168, 148, 1)',
    textColor: '#333',
    title: 'A Magical Friend'
  },
  {
    image: `${IMAGE_ROOT}/art/burton_book.png?v=${IMAGE_VERSION}`,
    screenMasked: `${IMAGE_ROOT}/art/burton_screen_masked.png?v=${IMAGE_VERSION}`,
    screenShadow: `${IMAGE_ROOT}/art/burton_screen_shadow.png?v=${IMAGE_VERSION}`,
    screenBackground: `${IMAGE_ROOT}/art/burton_screen_background.jpg?v=${IMAGE_VERSION}`,
    bgColor: 'rgba(88, 69, 126, 1)',
    textColor: '#FFF',
    title: 'Mom Needs Coffee'
  },
  {
    image: `${IMAGE_ROOT}/art/lego_book.png?v=${IMAGE_VERSION}`,
    screenMasked: `${IMAGE_ROOT}/art/lego_screen_masked.png?v=${IMAGE_VERSION}`,
    screenShadow: `${IMAGE_ROOT}/art/lego_screen_shadow.png?v=${IMAGE_VERSION}`,
    screenBackground: `${IMAGE_ROOT}/art/lego_screen_background.jpg?v=${IMAGE_VERSION}`,
    bgColor: 'rgba(229, 197, 67, 1)',
    textColor: '#333',
    title: 'Lego Ship'
  },
  {
    image: `${IMAGE_ROOT}/art/turkey_book.png?v=${IMAGE_VERSION}`,
    screenMasked: `${IMAGE_ROOT}/art/turkey_screen_masked.png?v=${IMAGE_VERSION}`,
    // screenShadow: `${IMAGE_ROOT}/art/turkey_screen_masked.png?v=${IMAGE_VERSION}`,
    screenBackground: `${IMAGE_ROOT}/art/turkey_screen_background.jpg?v=${IMAGE_VERSION}`,
    bgColor: 'rgba(131, 137, 188, 1)',
    textColor: '#333',
    title: 'Giving Thanks'
  },
  {
    image: `${IMAGE_ROOT}/art/robot_book.png?v=${IMAGE_VERSION}`,
    screenMasked: `${IMAGE_ROOT}/art/robot_screen_masked.png?v=${IMAGE_VERSION}`,
    screenShadow: `${IMAGE_ROOT}/art/robot_screen_shadow.png?v=${IMAGE_VERSION}`,
    screenBackground: `${IMAGE_ROOT}/art/robot_screen_background.jpg?v=${IMAGE_VERSION}`,
    bgColor: 'rgba(211, 105, 163, 1)',
    textColor: '#FFF',
    title: 'Love, Robot'
  },
  {
    image: `${IMAGE_ROOT}/art/gnome_book.png?v=${IMAGE_VERSION}`,
    screenMasked: `${IMAGE_ROOT}/art/gnome_screen_masked.png?v=${IMAGE_VERSION}`,
    screenShadow: `${IMAGE_ROOT}/art/gnome_screen_shadow.png?v=${IMAGE_VERSION}`,
    screenBackground: `${IMAGE_ROOT}/art/gnome_screen_background.jpg?v=${IMAGE_VERSION}`,
    bgColor: 'rgba(217, 180, 139, 1)',
    textColor: '#333',
    title: 'A Shy Gnome'
  },
  {
    image: `${IMAGE_ROOT}/art/dino_book.png?v=${IMAGE_VERSION}`,
    screenMasked: `${IMAGE_ROOT}/art/dino_screen_masked.png?v=${IMAGE_VERSION}`,
    screenShadow: `${IMAGE_ROOT}/art/dino_screen_shadow.png?v=${IMAGE_VERSION}`,
    screenBackground: `${IMAGE_ROOT}/art/dino_screen_background.jpg?v=${IMAGE_VERSION}`,
    bgColor: 'rgba(122, 163, 184, 1)',
    textColor: '#333',
    title: 'Happily Prehistoric'
  }
];

const initVariants = (page) => ({
  pageBackground: {
    default: {
      backgroundColor: '#FFF',
      transition: { duration: 0 }
    },
    active: {
      backgroundColor: page.bgColor,
      transition: {
        default: { duration: 0.8 }
      }
    }
  },
  pageImage: {
    default: {
      // scale: 0, Causes glitches when resetting
      opacity: 0,
      transition: { duration: 0 }
    },
    active: {
      // scale: 1,
      opacity: 1,
      transition: {
        layout: {
          type: 'spring',
          bounce: 0.6,
          duration: 0.6
        },
        default: {
          ease: 'backInOut',
          duration: 2
        }
      }
    }
  },
  pageText: {
    default: {
      scale: 0.3,
      opacity: 0,
      transition: { duration: 0 }
    },
    active: {
      scale: 1,
      opacity: 1,
      transition: {
        default: {
          type: 'spring',
          stiffness: 211,
          mass: 1,
          damping: 10
        },
        delay: (page.title.length - 3) * 0.2
      }
    }
  },
  sparkles: {
    default: {
      display: 'none',
      transition: { duration: 0 }
    },
    active: {
      display: 'block',
      transition: { duration: 0, delay: 0.5 }
    }
  },
  featuresText: {
    default: {
      display: 'block',
      transition: { duration: 0 }
    },
    active: {
      display: 'block',
      transition: {
        ease: 'backInOut',
        duration: 2
      }
    }
  }
});

const BookPage = React.forwardRef(({ page, pageIndex, ...props }, ref) => {
  const classes = makeClasses(styles);
  const theme = useTheme();
  const { currentSlide, setPageControls } = DemoConsumer();
  const { pageIndex: currentPageIndex } = currentSlide;
  const [variants, setVariants] = useState();
  const imageControls = useAnimationControls();
  const backgroundControls = useAnimationControls();
  const sparkleControls = useAnimationControls();
  const pageTyperControls = useAnimationControls();
  const isCurrentPage = currentPageIndex === pageIndex;

  useEffect(() => {
    setVariants(initVariants(page));
    setPageControls({
      pageTyperControls,
      imageControls,
      backgroundControls,
      sparkleControls,
      pageIndex
    }, pageIndex);
  }, []);

  /**
   * Reset the animations on this page if the page is NOT visible
   */
  useEffect(() => {
    // Only trigger resets on EVEN pages
    if (currentPageIndex % 2 === 0) {
      // Give it some time to let the page animate first
      // Otherwise it can be choppy
      setTimeout(() => {
        // Starting index of the previous page set that needs to be reset
        const resetIndex = currentPageIndex === 0
          ? PAGES.length - 2 // If we're at the beginning, get the last index
          : currentPageIndex - 2; // Otherwise it's the previous two
        // How many pages to include. If an odd number and at the beginning, just grab one
        const pagesToInclude = 2; // currentPageIndex === 0 && PAGES.length % 2 !== 0 ? 1 : 2;
        const pages = _.range(resetIndex, resetIndex + pagesToInclude, 1);
        if (pages.includes(pageIndex)) {
          pageTyperControls.set('default');
          imageControls.set('default');
          backgroundControls.set('default');
        }
      }, 1000);
    }
  }, [currentPageIndex]);

  if (!variants) return null;

  return (
    <div
      ref={ref}
      // The top-level page component needs a ref from the book
      // and doesn't work with motion components
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      style={{
        top: 0
      }}
    >
      <motion.div
        className={classNames(classes.bookPage, `page_${pageIndex}`, {
          isOdd: pageIndex % 2 !== 0,
          isEven: pageIndex % 2 === 0
        })}
        animate={backgroundControls}
        initial="default"
        variants={variants.pageBackground}
      >
        <Stack direction="column" height="100%" spacing="2">
          <Box
            className="pageImage"
            position="relative"
            width="100%"
          >
            <motion.div
              animate={imageControls}
              initial="default"
              variants={variants.pageImage}
              style={{
                position: 'relative',
                width: '100%',
                height: '100%'
              }}
            >
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%'
                }}
              >
                <Image
                  src={page.image}
                  layout="fill"
                  objectFit="contain"
                />
              </div>
            </motion.div>
            <motion.div
              className="sparkles"
              animate={sparkleControls}
              variants={variants.sparkles}
              initial="default"
            >
              {isCurrentPage && (
                <Sparkles
                  color={theme.palette.primary.main}
                  count={15}
                  minSize={7}
                  maxSize={15}
                  overflowPx={0}
                  fadeOutSpeed={30}
                  flicker={false}
                />
              )}
            </motion.div>
          </Box>
          <motion.div
            animate={pageTyperControls}
            initial="default"
            variants={variants.pageText}
            exit="default"
          >
            <Typography
              variant="bookPreview"
              color={page.textColor}
              className="pageTitle"
            >
              {page.title}
            </Typography>
          </motion.div>
        </Stack>
      </motion.div>
    </div>
  );
});

BookPage.propTypes = {
  page: PropTypes.shape().isRequired,
  pageIndex: PropTypes.number.isRequired
};

const Book = () => {
  const bookRef = useRef(null);
  const { setBookRef } = DemoConsumer();
  const classes = makeClasses(styles);

  const bookSize = BOOK_SIZES.lg;

  useEffect(() => {
    setBookRef(bookRef);
  }, []);

  return (
    <Box
      className={classes.book}
      style={{
        background: COVER_COLOR,
        width: bookSize.bookWidth + 20,
        height: bookSize.bookHeight + 20
      }}
    >
      <Box className="spine" />
      <HTMLFlipBook
        ref={bookRef}
        size="fixed"
        width={bookSize.bookWidth / 2}
        height={bookSize.bookHeight}
        usePortrait={false}
        flippingTime={500}
        disableFlipByClick
        useMouseEvents={false}
      >
        {PAGES.map((p, i) => (
          <BookPage
            key={p.image}
            page={p}
            pageIndex={i}
          />
        ))}
      </HTMLFlipBook>
    </Box>
  );
};

const BOOK_VARIANTS = {
  cta: {
    default: {
      opacity: 0,
      transition: {
        ease: 'backInOut',
        duration: 1
      }
    },
    appear: {
      opacity: 1,
      transition: {
        type: 'tween',
        ease: 'easeIn',
        duration: 1
      }
    }
  },
  boxPosition: {
    isLarge: {
      height: BOOK_SIZES.lg.bookHeight + 120,
      transition: {
        ease: 'backInOut',
        duration: 0.6
      }
    },
    isMedium: {
      height: BOOK_SIZES.lg.bookHeight + 150,
      transition: {
        ease: 'backInOut',
        duration: 0.6
      }
    },
    isSmall: {
      height: (BOOK_SIZES.lg.bookHeight + 150) * 0.7,
      transition: {
        ease: 'backInOut',
        duration: 0.6
      }
    }
  },
  bookPosition: {
    isLarge: {
      translateX: '-50%',
      translateY: '-50%',
      scale: 1,

      transition: {
        type: 'tween',
        ease: 'anticipate',
        duration: 1.2
      }
    },
    isMedium: {
      rotate: 0,
      left: '50%',
      translateX: '-50%',
      translateY: '-52%',
      scale: 1,
      transition: {
        type: 'tween',
        ease: 'anticipate',
        duration: 1.2
      }
    },
    isSmall: {
      left: '50%',
      translateX: '-55%',
      translateY: '-50%',
      scale: 0.7,
      transition: {
        type: 'tween',
        ease: 'anticipate',
        duration: 1.2
      }
    }
  },
  phonePosition: {
    isLarge: {
      left: 50,
      scale: 1,
      top: '50%',
      translateX: '0%',
      translateY: '-50%',
      transition: {
        type: 'tween',
        ease: 'anticipate',
        duration: 1.2
      }
    },
    isMedium: {
      left: '50%',
      scale: 1,
      top: '50%',
      translateY: '-40%',
      translateX: '-50%',
      transition: {
        type: 'tween',
        ease: 'anticipate',
        duration: 1.2
      }
    },
    isSmall: {
      left: 30,
      scale: 0.6,
      top: 0,
      translateY: 0,
      translateX: 0,
      transition: {
        type: 'tween',
        ease: 'anticipate',
        duration: 1.2
      }
    }
  }
};

const getControlKey = (isExtraSmall, isSmall, isMedium) => {
  let controlKey = 'isLarge';
  if (isMedium) controlKey = 'isLarge';
  else if (isSmall) controlKey = 'isMedium';
  else if (isExtraSmall) controlKey = 'isSmall';
  return controlKey;
};

const HeroDemo = ({ ctaClick, isHero, children }) => {
  const { track } = AnalyticsConsumer();
  const theme = useTheme();
  const classes = makeClasses(styles);
  const boxControls = useAnimationControls();
  const bookControls = useAnimationControls();
  const phoneControls = useAnimationControls();
  const ctaControls = useAnimationControls();

  // Prevent a double render by using the noSsr option
  const isExtraSmall = useMediaQuery(theme.breakpoints.up('xs'), { noSsr: true });
  const isSmall = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const isMedium = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });

  useEffect(() => {
    const controlKey = getControlKey(isExtraSmall, isSmall, isMedium);
    (async () => {
      bookControls.start(controlKey);
      phoneControls.start(controlKey);
      await boxControls.start(controlKey);
      // Disabled to reduce initial load strain
      // ctaControls.start('appear');
    })();
  }, [isSmall, isExtraSmall, isMedium]);

  const handleClick = () => {
    track(TRACK_HERO_DEMO_CTA_CLICKED);
    ctaClick();
  };

  const initialState = getControlKey(isExtraSmall, isSmall, isMedium);

  return (
    <Stack className={classes.landingBox}>
      {isHero && (
        <AppHeaderSpacer />
      )}
      <Grid
        container
        item
        maxWidth="lg"
        justifyContent="center"
      >
        <motion.div
          animate={ctaControls}
          initial="appear"
          variants={BOOK_VARIANTS.cta}
          style={{
            zIndex: 3,
            width: '100%',
            textAlign: 'center'
          }}
        >
          <Typography
            variant="bookPreview"
            className="headerText"
            component="h1"
            color="primary"
            sx={{
              mt: 2,
              mx: 'auto'
            }}
          >
            {TITLE}
          </Typography>
          <Typography
            variant="bookPreview"
            className="subHeaderText"
            component="h2"
            color="notBlack"
            sx={{
              mt: 6,
              maxWidth: '90%',
              mx: 'auto'
            }}
          >
            {SUBTITLE}
          </Typography>
        </motion.div>
        <motion.div
          className={classes.demoBox}
          style={{
            position: 'relative',
            overflow: 'hidden'
          }}
          layout
          animate={boxControls}
          initial={initialState}
          variants={BOOK_VARIANTS.boxPosition}
        >
          <motion.div
            // layout
            animate={bookControls}
            initial={initialState}
            variants={BOOK_VARIANTS.bookPosition}
            style={{
              position: 'absolute',
              width: BOOK_SIZES.lg.bookWidth + 20,
              height: BOOK_SIZES.lg.bookHeight + 20,
              left: '50%',
              top: '50%'
            }}
          >
            <Book />
          </motion.div>
          <motion.div
            // layout
            animate={phoneControls}
            initial={initialState}
            variants={BOOK_VARIANTS.phonePosition}
            style={{
              height: PHONE_SIZES.lg.phoneHeight,
              width: PHONE_SIZES.lg.phoneWidth,
              position: 'absolute',
              left: 0
            }}
          >
            <Phone pages={PAGES} />
          </motion.div>
        </motion.div>
        <motion.div
          className={classes.ctaBox}
          animate={ctaControls}
          initial="appear"
          variants={BOOK_VARIANTS.cta}
        >
          {children || (
            <>
              <Button
                variant="contained"
                color="primary"
                size="large"
                alt="Try designing your book for free"
                onClick={handleClick}
                sx={{
                  py: 2,
                  px: 8,
                  fontSize: '1.1rem'
                }}
              >
                Create Yours Now
              </Button>
              <Link
                href={{
                  pathname: PATH_FAQ_SHIPPING
                }}
                passHref
              >
                <Button
                  alt="Free shipping info"
                  startIcon={<LocalShippingIcon />}
                  color="notBlack"
                  style={{
                    mt: 4,
                    color: '#333',
                    textTransform: 'none'
                  }}
                >
                  Free US Shipping
                </Button>
              </Link>
            </>
          )}
        </motion.div>
      </Grid>
    </Stack>
  );
};

HeroDemo.defaultProps = {
  ctaClick: null,
  isHero: true,
  children: null
};

HeroDemo.propTypes = {
  ctaClick: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  isHero: PropTypes.bool
};

const HeroWithContext = (props) => (
  <DemoProvider>
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <HeroDemo {...props} />
  </DemoProvider>
);

export default HeroWithContext;
